/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import ContentShell from "components/ContentShell";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "hooks/useSnackbar";
import useConsent from "hooks/useConsent";
import configData from "config/config.json";
import { makeStyles } from "@mui/styles";
import { Button, CircularProgress } from "@mui/material";
import FormSelectInput from "components/FormSelectInput";
import FormTextInput from "components/FormTextInput";
import SimpleSwitch from "components/Switch";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import { Formik, Form } from "formik";
import * as yup from "yup";
import { CMSUserRoleOptions } from "utils/tableHelper/userManagementTableHelper";
import {
  createUser,
  getUserInfo,
  updateUserRole,
  updateUserStatus,
  forcePasswordReset,
  deleteUser,
} from "services/userManagementService";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "24px",
    maxWidth: "1400px",
    margin: "0 auto",
    height: "calc(100vh - 120px)",
    display: "flex",
    flexDirection: "column",
    background: "linear-gradient(135deg, #f5f7fa 0%, #f9fafb 100%)",
  },
  headerSection: {
    marginBottom: "24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 24px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: "12px",
    boxShadow: "0 4px 20px rgba(102, 126, 234, 0.25)",
  },
  titleGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  title: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#ffffff",
    margin: 0,
    letterSpacing: "-0.5px",
  },
  subtitle: {
    fontSize: "14px",
    color: "rgba(255, 255, 255, 0.9)",
    margin: 0,
    fontWeight: 400,
  },
  headerActions: {
    display: "flex",
    gap: "10px",
  },
  mainContent: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "24px",
    flex: 1,
  },
  leftColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  rightColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  card: {
    backgroundColor: "#ffffff",
    padding: "24px",
    borderRadius: "12px",
    border: "none",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow:
        "0 4px 12px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08)",
    },
  },
  cardHeader: {
    fontSize: "13px",
    fontWeight: 700,
    color: "#4a5568",
    marginBottom: "20px",
    paddingBottom: "12px",
    borderBottom: "2px solid #e2e8f0",
    textTransform: "uppercase",
    letterSpacing: "1px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "130px 1fr",
    gap: "16px 20px",
    alignItems: "center",
  },
  infoLabel: {
    fontSize: "13px",
    color: "#718096",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.3px",
  },
  infoValue: {
    fontSize: "15px",
    color: "#2d3748",
    fontWeight: 500,
    padding: "8px 12px",
    backgroundColor: "#f7fafc",
    borderRadius: "6px",
    border: "1px solid #e2e8f0",
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 14px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  },
  statusActive: {
    backgroundColor: "#48bb78",
    color: "#ffffff",
  },
  statusInactive: {
    backgroundColor: "#f56565",
    color: "#ffffff",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
  },
  actionCard: {
    backgroundColor: "#f7fafc",
    padding: "18px",
    borderRadius: "10px",
    border: "2px solid #e2e8f0",
    marginBottom: "12px",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: "#cbd5e0",
      backgroundColor: "#edf2f7",
    },
  },
  actionTitle: {
    fontSize: "13px",
    fontWeight: 700,
    color: "#4a5568",
    marginBottom: "12px",
    letterSpacing: "0.3px",
    textTransform: "uppercase",
  },
  actionButtons: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },
  buttonPrimary: {
    minWidth: "120px",
    textTransform: "none",
    fontSize: "13px",
    fontWeight: 600,
    padding: "8px 16px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(102, 126, 234, 0.25)",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "linear-gradient(135deg, #5568d3 0%, #6a4193 100%)",
      boxShadow: "0 4px 12px rgba(102, 126, 234, 0.35)",
      transform: "translateY(-2px)",
    },
  },
  buttonSecondary: {
    minWidth: "110px",
    textTransform: "none",
    fontSize: "14px",
    fontWeight: 600,
    padding: "10px 20px",
    borderColor: "#cbd5e0",
    backgroundColor: "#ffffff",
    color: "#4a5568",
    borderRadius: "8px",
    borderWidth: "2px",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: "#a0aec0",
      backgroundColor: "#f7fafc",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    },
  },
  buttonWarning: {
    minWidth: "140px",
    textTransform: "none",
    fontSize: "14px",
    fontWeight: 600,
    padding: "10px 20px",
    borderColor: "#f6ad55",
    backgroundColor: "#ffffff",
    color: "#ed8936",
    borderRadius: "8px",
    borderWidth: "2px",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: "#ed8936",
      backgroundColor: "#fffaf0",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(237, 137, 54, 0.25)",
    },
  },
  buttonDanger: {
    minWidth: "110px",
    textTransform: "none",
    fontSize: "14px",
    fontWeight: 600,
    padding: "10px 20px",
    borderColor: "#fc8181",
    backgroundColor: "#ffffff",
    color: "#f56565",
    borderRadius: "8px",
    borderWidth: "2px",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: "#f56565",
      backgroundColor: "#fff5f5",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(245, 101, 101, 0.25)",
    },
  },
  createFormContainer: {
    maxWidth: "900px",
    margin: "0 auto",
  },
  createFormGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
    marginBottom: "20px",
  },
  createFormActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    height: "100%",
  },
  headerBackButton: {
    minWidth: "120px",
    textTransform: "none",
    fontSize: "14px",
    fontWeight: 600,
    padding: "10px 24px",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    color: "#ffffff",
    borderRadius: "8px",
    border: "2px solid rgba(255, 255, 255, 0.3)",
    backdropFilter: "blur(10px)",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.3)",
      borderColor: "rgba(255, 255, 255, 0.5)",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    },
  },
  dangerZoneCard: {
    border: "2px solid #fed7d7",
    backgroundColor: "#fffaf0",
  },
  iconWrapper: {
    width: "24px",
    height: "24px",
    borderRadius: "6px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    color: "#ffffff",
    fontWeight: 700,
  },
}));

const validationSchema = yup.object().shape({
  username: yup
    .string()
    .required("Username is required")
    .min(3, "Username must be at least 3 characters"),
  email: yup
    .string()
    .email("Email must be valid")
    .required("Email is required"),
  role: yup.string().required("Role is required"),
});

const UserEditor = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const { showSnackbar } = useSnackbar();
  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();

  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);

  const isCreate = !id;

  // Fetch user data if in edit mode
  useEffect(() => {
    if (id) {
      fetchUserData(id);
    }
  }, [id]);

  const fetchUserData = async (userId) => {
    try {
      setLoading(true);
      const response = await getUserInfo(userId);
      if (response?.status && response?.data) {
        setUser(response.data);
      }
    } catch (error) {
      console.error("Error fetching user:", error);
      showSnackbar(error?.message || "Failed to fetch user data", "error");
      navigate(configData.routes.userManagement.list);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLoading(true);

      const response = await createUser({
        username: values.username,
        email: values.email,
        role: values.role,
        isActive: values.isActive,
      });

      if (response?.status) {
        showSnackbar("User created successfully", "success");
        navigate(configData.routes.userManagement.list);
      }
    } catch (error) {
      console.error("Error saving user:", error);
      showSnackbar(error?.message || "Failed to save user", "error");
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  // Handle role update with confirmation
  const handleRoleUpdate = (newRole: any) => {
    updateDetails({
      title: "Update User Role",
      itemName: user?.username,
      message: `Are you sure you want to change the role of "${
        user?.username
      }" to "${CMSUserRoleOptions.find((r) => r.id === newRole)?.label}"?`,
      onConfirm: () => confirmRoleUpdate(newRole),
    });
    openConsent();
  };

  const confirmRoleUpdate = async (newRole) => {
    closeConsent();
    try {
      setLoading(true);
      const response = await updateUserRole(id, newRole);
      if (response?.status) {
        showSnackbar("User role updated successfully", "success");
        await fetchUserData(id);
      }
    } catch (error) {
      console.error("Error updating role:", error);
      showSnackbar(error?.message || "Failed to update role", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle status update with confirmation
  const handleStatusUpdate = (newStatus: any) => {
    updateDetails({
      title: "Update User Status",
      itemName: user?.username,
      message: `Are you sure you want to ${
        newStatus ? "activate" : "deactivate"
      } user "${user?.username}"?`,
      onConfirm: () => confirmStatusUpdate(newStatus),
    });
    openConsent();
  };

  const confirmStatusUpdate = async (newStatus) => {
    closeConsent();
    try {
      setLoading(true);
      const response = await updateUserStatus(id, newStatus);
      if (response?.status) {
        showSnackbar("User status updated successfully", "success");
        await fetchUserData(id);
      }
    } catch (error) {
      console.error("Error updating status:", error);
      showSnackbar(error?.message || "Failed to update status", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle force password reset
  const handleForcePasswordReset = () => {
    updateDetails({
      title: "Force Password Reset",
      itemName: user?.email,
      message: `Send password reset email to ${user?.email}?`,
      onConfirm: confirmForcePasswordReset,
    });
    openConsent();
  };

  const confirmForcePasswordReset = async () => {
    closeConsent();
    try {
      setLoading(true);
      const response = await forcePasswordReset(id);
      if (response?.status) {
        showSnackbar("Password reset email sent successfully", "success");
        await fetchUserData(id);
      }
    } catch (error) {
      console.error("Error sending reset email:", error);
      showSnackbar(error?.message || "Failed to send reset email", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle delete user
  const handleDeleteUser = () => {
    updateDetails({
      title: "Delete User",
      itemName: user?.username,
      message: `Are you sure you want to delete user "${user?.username}"? This action cannot be undone.`,
      onConfirm: confirmDeleteUser,
    });
    openConsent();
  };

  const confirmDeleteUser = async () => {
    closeConsent();
    try {
      setLoading(true);
      const response = await deleteUser(id);
      if (response?.status) {
        showSnackbar("User deleted successfully", "success");
        navigate(configData.routes.userManagement.list);
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      showSnackbar(error?.message || "Failed to delete user", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(configData.routes.userManagement.list);
  };

  const initialValues = isCreate
    ? {
        username: "",
        email: "",
        role: "",
        isActive: true,
      }
    : {
        username: user?.username || "",
        email: user?.email || "",
        role: user?.role || "",
        isActive: user?.isActive !== false,
      };

  return (
    <ContentShell loading={loading}>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.headerSection}>
          <div className={styles.titleGroup}>
            <h1 className={styles.title}>
              {isCreate
                ? "✨ Create New User"
                : `👤 Edit User: ${user?.username || ""}`}
            </h1>
            <p className={styles.subtitle}>
              {isCreate ? "Add a new user to the system" : user?.email || ""}
            </p>
          </div>
          <div className={styles.headerActions}>
            <Button
              onClick={handleCancel}
              variant="outlined"
              size="medium"
              className={styles.headerBackButton}
            >
              {isCreate ? "← Cancel" : "← Back to List"}
            </Button>
          </div>
        </div>

        {/* Create Form (Centered) */}
        {isCreate ? (
          <div className={styles.createFormContainer}>
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <div className={styles.iconWrapper}>📝</div>
                User Details
              </div>
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  setFieldValue,
                  isSubmitting,
                }) => (
                  <Form>
                    <div className={styles.createFormGrid}>
                      <FormTextInput
                        label="Username"
                        name="username"
                        value={values.username}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.username && Boolean(errors.username)}
                        helperText={touched.username && errors.username}
                        required
                        placeholder="Enter username"
                      />

                      <FormTextInput
                        label="Email"
                        name="email"
                        type="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                        required
                        placeholder="Enter email address"
                      />

                      <FormSelectInput
                        label="Role"
                        name="role"
                        value={values.role}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        options={CMSUserRoleOptions}
                        error={touched.role && Boolean(errors.role)}
                        helperText={touched.role && errors.role}
                        required
                      />

                      <SimpleSwitch
                        legendText="Account Status"
                        name="isActive"
                        value={values.isActive}
                        label={values.isActive ? "Active" : "Inactive"}
                        onChange={() =>
                          setFieldValue("isActive", !values.isActive)
                        }
                      />

                      <div className={styles.createFormActions}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="medium"
                          disabled={isSubmitting || loading}
                          className={styles.buttonPrimary}
                        >
                          {loading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            "✓ Create User"
                          )}
                        </Button>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        ) : (
          /* Edit Mode - 2x2 Grid Layout */
          user && (
            <div className={styles.mainContent}>
              {/* Left Column */}
              <div className={styles.leftColumn}>
                {/* Role Update */}
                <div className={styles.card}>
                  <div className={styles.cardHeader}>
                    <div className={styles.iconWrapper}>🔧</div>
                    Update Role
                  </div>
                  <Formik
                    initialValues={{ role: user.role }}
                    enableReinitialize
                    onSubmit={() => {}}
                  >
                    {({ values, handleChange, handleBlur }) => (
                      <Form>
                        <FormSelectInput
                          label="Select New Role"
                          name="role"
                          value={values.role}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          options={CMSUserRoleOptions}
                          required
                        />
                        <div
                          className={styles.actionButtons}
                          style={{ marginTop: "16px" }}
                        >
                          <Button
                            variant="contained"
                            size="medium"
                            className={styles.buttonPrimary}
                            disabled={loading || values.role === user.role}
                            onClick={() => handleRoleUpdate(values.role)}
                          >
                            {loading ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : (
                              "✓ Update Role"
                            )}
                          </Button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>

                {/* Force Password Reset */}
                <div className={styles.card}>
                  <div className={styles.cardHeader}>
                    <div className={styles.iconWrapper}>🔐</div>
                    Force Password Reset
                  </div>
                  <div className={styles.actionCard}>
                    <div className={styles.actionTitle}>
                      Send password reset email to user
                    </div>
                    <div className={styles.actionButtons}>
                      <Button
                        variant="outlined"
                        size="medium"
                        className={styles.buttonWarning}
                        disabled={loading}
                        onClick={handleForcePasswordReset}
                      >
                        📧 Send Reset Email
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className={styles.rightColumn}>
                {/* Status Update */}
                <div className={styles.card}>
                  <div className={styles.cardHeader}>
                    <div className={styles.iconWrapper}>�</div>
                    Update Status
                  </div>
                  <Formik
                    initialValues={{ isActive: user.isActive }}
                    enableReinitialize
                    onSubmit={() => {}}
                  >
                    {({ values, setFieldValue }) => (
                      <Form>
                        <SimpleSwitch
                          legendText="Account Status"
                          name="isActive"
                          value={values.isActive}
                          label={values.isActive ? "Active" : "Inactive"}
                          onChange={() =>
                            setFieldValue("isActive", !values.isActive)
                          }
                        />
                        <div
                          className={styles.actionButtons}
                          style={{ marginTop: "16px" }}
                        >
                          <Button
                            variant="contained"
                            size="medium"
                            className={styles.buttonPrimary}
                            disabled={
                              loading || values.isActive === user.isActive
                            }
                            onClick={() => handleStatusUpdate(values.isActive)}
                          >
                            {loading ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : (
                              "✓ Update Status"
                            )}
                          </Button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>

                {/* Delete User */}
                <div className={`${styles.card} ${styles.dangerZoneCard}`}>
                  <div className={styles.cardHeader}>
                    <div className={styles.iconWrapper}>�️</div>
                    Delete User Account
                  </div>
                  <div className={styles.actionCard}>
                    <div className={styles.actionTitle}>
                      Permanently delete this user account
                    </div>
                    <div className={styles.actionButtons}>
                      <Button
                        variant="outlined"
                        size="medium"
                        className={styles.buttonDanger}
                        disabled={loading}
                        onClick={handleDeleteUser}
                      >
                        ✕ Delete User
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        )}

        {/* Confirmation Dialog */}
        {isOpenConsent && (
          <DeleteConsentDialog
            open={isOpenConsent}
            onClose={closeConsent}
            onConfirm={constentDetails?.onConfirm}
            itemName={constentDetails?.itemName || "this item"}
            message={constentDetails?.message}
            btnText={constentDetails?.title || "Confirm"}
          />
        )}
      </div>
    </ContentShell>
  );
};

export default UserEditor;
